import React, { useState } from 'react';
import { getCardEffectDescription } from '../../utils/effectFormatter';
import './CardComponent.css';

const CardComponent = ({ 
  card, 
  onClick,
  flippable = false,
  animated = false,
  highlighted = false,
  size = 'medium',
  showDetails = true
}) => {
  const [flipped, setFlipped] = useState(false);
  
  const handleClick = () => {
    if (flippable) {
      setFlipped(!flipped);
    }
    if (onClick) {
      onClick(card);
    }
  };
  
  // Slug décoratif pour les classes CSS d'élément (fire, dark…)
  const elementSlug = (element) => (element || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-');

  const getElementColor = (element) => {
    const elementColors = {
      // Clés FR (legacy)
      'Feu': 'var(--color-fire)',
      'Eau': 'var(--color-water)',
      'Terre': 'var(--color-earth)',
      'Vent': 'var(--color-air)',
      'Lumière': 'var(--color-light)',
      'Ténèbres': 'var(--color-dark)',
      // Clés EN (API)
      'FIRE': 'var(--color-fire)',
      'WATER': 'var(--color-water)',
      'EARTH': 'var(--color-earth)',
      'AIR': 'var(--color-air)',
      'LIGHT': 'var(--color-light)',
      'DARK': 'var(--color-dark)',
      'Arc-en-Ciel': 'linear-gradient(to right, #ff6b3d, #ffb454, #f2e5a0, #b8f5d0, #3fb6ff, #9b8cff)'
    };
    return elementColors[element] || 'var(--color-primary)';
  };
  
  // Classe décorative de rareté (R54 : COMMON/RARE/EPIC/LEGENDARY + legacy lettres)
  const getRarityClass = (rarity) => {
    switch (rarity) {
      case 'C': case 'N': case 'COMMON': case 'NORMAL': return 'common';
      case 'R': case 'RARE': return 'rare';
      case 'E': case 'SR': case 'EPIC': case 'SUPER_RARE': return 'epic';
      case 'L': case 'SSR': case 'LEGENDARY': case 'SUPER_SUPER_RARE': return 'legendary';
      case 'AC': case 'RAINBOW': return 'rainbow';
      default: return '';
    }
  };
  
  if (!card) return null;
  
  return (
    <div 
      className={`card-component element-${elementSlug(card.element)} ${size} ${flipped ? 'flipped' : ''} ${animated ? 'animated' : ''} ${highlighted ? 'highlighted' : ''} rarity-${getRarityClass(card.rarity)}`}
      onClick={handleClick}
    >
      <div className="card-inner">
        <div className="card-front">
          <div 
            className="card-border"
            style={{ 
              borderColor: getElementColor(card.element),
              background: card.rarity === 'AC' ? 'transparent' : undefined
            }}
          >
            <div className="card-image-container">
              <div className="card-element-badge" style={{ backgroundColor: getElementColor(card.element) }}>
                {card.element}
              </div>
              <img 
                src={card.imageUrl || `/api/static/images/elements/${card.element.toLowerCase()}.svg`} 
                alt={card.name}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `/api/static/images/card-backs/${card.element.toLowerCase()}-back.svg`;
                }}
              />
              {card.rarity === 'L' && <div className="card-glow legendary-glow"></div>}
              {card.rarity === 'AC' && <div className="card-glow rainbow-glow"></div>}
            </div>
            
            {showDetails && (
              <div className="card-details">
                <h3 className="card-name">{card.name}</h3>
                
                <div className="card-type-rarity">
                  <span className="card-type">{card.type}</span>
                  <span className={`card-rarity rarity-${getRarityClass(card.rarity)}`}>{card.rarity}</span>
                </div>
                
                {card.type === 'CHARACTER' && (
                  <div className="card-stats">
                    <div className="stat">
                      <span className="stat-label">ATK</span>
                      <span className="stat-value">{card.attack}</span>
                    </div>
                    <div className="stat">
                      {/* "defense" = PV dans le moteur (une seule stat défensive) */}
                      <span className="stat-label">PV</span>
                      <span className="stat-value">{card.defense}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">COST</span>
                      <span className="stat-value">{card.cost}</span>
                    </div>
                  </div>
                )}
                
                {card.type !== 'CHARACTER' && (
                  <div className="card-cost">
                    <span className="cost-label">COST</span>
                    <span className="cost-value">{card.cost}</span>
                  </div>
                )}
                
                <div className="card-description">
                  <p>{getCardEffectDescription(card)}</p>
                </div>
                
                {(card.flavorText || card.description) && (
                  <div className="card-flavor">
                    <em>{card.flavorText || card.description}</em>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {flippable && (
          <div className="card-back">
            <div className="card-back-design">
              <div 
                className="card-back-element" 
                style={{ backgroundImage: `url(/card-backs/${card.element.toLowerCase()}.png)` }}
              ></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CardComponent;